import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "wouter";

const STORAGE_KEY = "bsp-cookie-consent";
const GOOGLE_ANALYTICS_ID = "G-ZM0NW298EB";

function enableGoogleAnalytics() {
  const analyticsWindow = window as Window & {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  };
  if (analyticsWindow.gtag) return;

  const dataLayer = (analyticsWindow.dataLayer = analyticsWindow.dataLayer || []);
  analyticsWindow.gtag = (...args: unknown[]) => dataLayer.push(args);
  analyticsWindow.gtag("js", new Date());
  analyticsWindow.gtag("config", GOOGLE_ANALYTICS_ID);

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;
  document.head.appendChild(script);
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "accepted") {
        enableGoogleAnalytics();
      } else if (!stored) {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const handleChoice = (value: "accepted" | "declined") => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Ignore storage errors (for example, private browsing).
    }
    if (value === "accepted") {
      enableGoogleAnalytics();
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          className="fixed bottom-4 left-4 right-4 z-[60] md:left-auto md:right-6 md:bottom-6 md:max-w-md"
        >
          <div className="bg-zinc-950/95 backdrop-blur border border-white/10 rounded-2xl p-6 shadow-2xl">
            <h2 className="font-display text-lg font-bold uppercase tracking-tight mb-2">
              We value your privacy
            </h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              We use cookies to enhance your browsing experience and analyse our
              traffic. By clicking "Accept", you consent to our use of cookies.
              Read our{" "}
              <Link
                href="/privacy-policy"
                className="text-primary hover:underline"
              >
                Privacy Policy
              </Link>{" "}
              to learn more.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => handleChoice("accepted")}
                className="flex-1 bg-primary-strong text-white text-sm font-semibold uppercase tracking-widest py-3 rounded-full hover:bg-primary-strong/80 transition-colors duration-300"
              >
                Accept
              </button>
              <button
                type="button"
                onClick={() => handleChoice("declined")}
                className="flex-1 border border-white/20 text-gray-300 text-sm font-semibold uppercase tracking-widest py-3 rounded-full hover:bg-white/5 transition-colors duration-300"
              >
                Decline
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
